// propTypes can't be used with forwardRef
/* eslint-disable react/prop-types */

import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import Downshift from 'downshift';
import { useThrottle } from 'react-use';

import * as Sentry from '@sentry/gatsby';
import { useField } from 'formik';
import {
  inputStylesError,
  inputStylesNoError,
} from '~components/forms/inputs/Field';
import SmartyAutocomplete from '~utils/smartyAutocomplete';

function AutocompleteInput(
  {
    onInputChange,
    onAutocompleteSelect,
    onBlur,
    name,
    required,
    elementId,
    placeholder,
    errors,
    hasManualOption,
  },
  ref
) {
  const autocompleteClient = new SmartyAutocomplete();

  const [field, meta] = useField(name);
  const error = !!(meta.touched && meta.error) || errors?.[name];
  const manualOption = {
    addressText: 'Enter address manually',
  };
  const baseOptionList = hasManualOption ? [manualOption] : [];
  const [queryResults, setQueryResults] = useState(baseOptionList);
  const [isOpen, setIsOpen] = useState(!!field.value);
  const [isManualSelected, setIsManualSelected] = useState(false);
  const inputEl = useRef(null);

  // Expose a handle that the parent can use to control the input
  useImperativeHandle(ref, () => ({
    focus: () => inputEl.current.focus(),
    blur: () => inputEl.current.blur(),
    setIsOpen,
  }));

  // We don't want to hit mapbox on every keystroke, so we throttle to only
  // query every 250ms while the user is typing.
  const debouncedValue = useThrottle(field.value, 250);

  useEffect(() => {
    // Because the request to mapbox can take >250ms, it's possible that we
    // still have a request in-flight when the user starts typing again. So we
    // cancel the request any time we make a new request.
    let cancelled = false;

    if (debouncedValue?.length >= 2) {
      autocompleteClient
        .getSuggestions(debouncedValue)
        .then((results) => {
          if (cancelled) {
            return;
          }
          const values = autocompleteClient.filterSuggestions(results.result);
          if (hasManualOption) {
            setQueryResults([manualOption].concat(values));
          } else {
            setQueryResults(values);
          }
        })
        .catch((err) => {
          if (cancelled) {
            return;
          }
          // eslint-disable-next-line no-console
          console.warn('Error from Smarty', err);
          Sentry.captureException(err);
          setQueryResults(baseOptionList);
        });
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, hasManualOption]);

  const handleChange = (evt) => {
    const newQuery = evt.target.value;

    setIsOpen(true);
    onInputChange(newQuery);
  };

  const handleSelect = (place) => {
    const manualSelected = place.addressText === 'Enter address manually';
    if (manualSelected) {
      setIsManualSelected(true);
    }

    const placeName = autocompleteClient.formatSuggestion(place);
    if (!placeName && !manualSelected) {
      return;
    }

    if (placeName) {
      onInputChange(placeName);
    }

    setIsOpen(false);
    inputEl.current.blur();
    onAutocompleteSelect(place);
  };

  return (
    <Downshift
      onChange={handleSelect}
      onOuterClick={() => setIsOpen(false)}
      itemToString={(item) => (item ? item.addressText : '')}
      isOpen={isOpen}
      inputValue={field.value || ''}
      labelId={`${name}-label`}
      inputId={name}
    >
      {({ getInputProps, getItemProps, highlightedIndex }) => {
        return (
          <span>
            <input
              className={
                error
                  ? `${inputStylesError} block`
                  : `${inputStylesNoError} block`
              }
              ref={inputEl}
              {...getInputProps({
                placeholder,
                onChange: handleChange,
              })}
              onBlur={onBlur}
              required={required}
              data-element-id={elementId}
              // Making this a search-type field prevents the browser and/or its extensions from suggesting items
              // to fill in here. Previously, the browser suggestions were obscuring our address suggestions.
              // https://stackoverflow.com/a/58640929
              type='search'
            />
            {queryResults.length > baseOptionList.length &&
              isOpen &&
              !isManualSelected && (
                <div className='relative inline-block h-0 w-full'>
                  <div className='dropdown absolute z-50 -mt-4 w-full rounded-lg border bg-base-100'>
                    {queryResults.map((item, index) => (
                      <div
                        className={`cursor-pointer px-6 py-2 text-sm ${
                          highlightedIndex === index && 'bg-base-200'
                        } ${item.addressText === 'Enter address manually' && 'font-bold'}`}
                        {...getItemProps({
                          key: `${item.streetLine + item.secondary + item.city + item.state + item.zipcode}`,
                          index,
                          item,
                        })}
                      >
                        {item.addressText ??
                          autocompleteClient.formatSuggestion(item)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </span>
        );
      }}
    </Downshift>
  );
}
export default forwardRef(AutocompleteInput);
